/**
 * Thin axios client for the erp-integration-api secure_proxy whitelist
 * endpoints consumed by the admin portal (staff-only).
 *
 * Mirrors the shape of src/api/query-api-client.ts and src/api/organization-client.ts:
 *   - module-scoped memoized axios instance
 *   - baseURL + internal-auth Bearer token injected via `configureClient`
 *   - `useErpIntegrationClient` hook returns a stable reference
 *
 * Cross-org access: admin-portal internal-auth tokens with empty `assume_roles`
 * bypass the permissions check server-side, but the backend still resolves the
 * target org via `getOrganizationId(c)` which reads the `x-ivy-org-id` header
 * (falling back to the token's `org_id`). Every method therefore accepts an
 * explicit `orgId` and injects `x-ivy-org-id` on the request. Without this,
 * calls would operate on the admin-portal org instead of the target org.
 *
 * Types (SecureProxyWhitelist, SecureProxyWhitelistUpdate, SecureProxyHistoryRow)
 * are the single source of truth for every downstream Phase-2 plan. Keep them in
 * sync with the backend openapi.yml in erp-integration-api:
 *   lambda/ApiHandlerFunction/openapi.yml  (schemas: SecureProxyWhitelist,
 *     SecureProxyWhitelistUpdate, SecureProxyUseCaseHistoryEntry,
 *     UseCaseHistoryEntryBase).
 */
import axios, { AxiosInstance } from 'axios'
import { useMemo } from 'react'

import { config } from '../configs/api.config'

import { configureClient } from './api-utils'

// --- types ------------------------------------------------------------------

export interface SecureProxyWhitelist {
  allowed_domains: string[]
  allowed_ips: string[]
  /** VPC routing mode (read-only). `null` if not yet set. */
  vpc_mode: 'static_ip' | 'secure_link' | null
}

export interface SecureProxyWhitelistUpdate {
  allowed_domains?: string[]
  allowed_ips?: string[]
}

/**
 * A single entry from the whitelist history endpoint.
 *
 * Matches the backend `SecureProxyUseCaseHistoryEntry` schema (allOf
 * `UseCaseHistoryEntryBase` + secure_proxy configuration). The backend returns
 * full configuration snapshots per entry; Plan 04 (UI-08) derives a
 * before/after diff by comparing consecutive entries client-side.
 */
export interface SecureProxyHistoryRow {
  /** Unique identifier for this history entry */
  id: string
  /** ISO-8601 timestamp when this history entry was created */
  history_created_at: string
  /** User id / email of the user who made the change (from internal-auth) */
  changed_by?: string
  /** Free-text description of the change */
  change_description?: string
  /** Reference to the parent use case */
  useCaseId: string
  /** Parent integration id */
  integrationId: string
  /** Use case name at this point in history */
  name: string
  /** Always `secure_proxy` for entries from this endpoint */
  type: 'secure_proxy'
  /** Whether the use case was enabled at this point in history */
  enabled: boolean
  /** Snapshot of the whitelist configuration at this point in history */
  configuration?: {
    vpc_mode?: 'static_ip' | 'secure_link'
    allowed_domains?: string[]
    allowed_ips?: string[]
  }
}

/**
 * Minimal use-case shape returned alongside an integration by
 * `listIntegrationsV2`. Fields beyond the ones listed here are intentionally
 * omitted — the admin portal only needs identity + type to filter secure_proxy
 * entries.
 */
export interface IntegrationUseCaseSummary {
  id: string
  name: string
  slug?: string
  type: 'inbound' | 'outbound' | 'file_proxy' | 'managed_call' | 'secure_proxy'
}

/**
 * Shape of an integration as returned by `listIntegrationsV2`. Matches the
 * backend `IntegrationWithUseCases` schema — `use_cases` is always present
 * (required per openapi.yml) and may be an empty array.
 */
export interface IntegrationWithUseCasesSummary {
  id: string
  name: string
  use_cases: IntegrationUseCaseSummary[]
}

/**
 * Thrown by `getSecureProxyHistory` when the backend history endpoint responds
 * 404 or 501 — i.e. not yet deployed on the current stage. Callers (Plan 04)
 * use this to defer the history panel gracefully without surfacing a raw error.
 */
export class HistoryEndpointNotAvailableError extends Error {
  constructor(message = 'History endpoint not available') {
    super(message)
    this.name = 'HistoryEndpointNotAvailableError'
  }
}

// --- memoized axios factory -------------------------------------------------

let client: AxiosInstance | undefined

const getAxios = (): AxiosInstance => {
  if (!client) {
    client = axios.create({
      baseURL: config.ERP_INTEGRATIONS
    })

    configureClient(client, {
      baseURL: config.ERP_INTEGRATIONS
    })
  }

  return client
}

/**
 * Build the cross-org request config. Always emits `x-ivy-org-id` so the
 * backend resolves the correct target organization regardless of which org the
 * internal-auth token was minted for.
 */
const withOrgHeader = (orgId: string) => ({
  headers: { 'x-ivy-org-id': orgId }
})

// --- public API -------------------------------------------------------------

export const getErpIntegrationClient = () => {
  const c = getAxios()

  return {
    listIntegrationsV2: async (
      orgId: string
    ): Promise<IntegrationWithUseCasesSummary[]> => {
      const response = await c.get<{
        integrations: IntegrationWithUseCasesSummary[]
      }>('/v2/integrations', withOrgHeader(orgId))

      return response.data.integrations ?? []
    },

    getSecureProxyWhitelist: async (
      orgId: string,
      integrationId: string,
      useCaseId: string
    ): Promise<SecureProxyWhitelist> => {
      const encodedIntegrationId = encodeURIComponent(integrationId)
      const encodedUseCaseId = encodeURIComponent(useCaseId)
      const response = await c.get<SecureProxyWhitelist>(
        `/v2/integrations/${encodedIntegrationId}/use-cases/${encodedUseCaseId}/secure-proxy-whitelist`,
        withOrgHeader(orgId)
      )

      return response.data
    },

    updateSecureProxyWhitelist: async (
      orgId: string,
      integrationId: string,
      useCaseId: string,
      body: SecureProxyWhitelistUpdate
    ): Promise<SecureProxyWhitelist> => {
      const encodedIntegrationId = encodeURIComponent(integrationId)
      const encodedUseCaseId = encodeURIComponent(useCaseId)
      const response = await c.put<SecureProxyWhitelist>(
        `/v2/integrations/${encodedIntegrationId}/use-cases/${encodedUseCaseId}/secure-proxy-whitelist`,
        body,
        withOrgHeader(orgId)
      )

      return response.data
    },

    getSecureProxyHistory: async (
      orgId: string,
      integrationId: string,
      useCaseId: string,
      limit: number = 10
    ): Promise<SecureProxyHistoryRow[]> => {
      const encodedIntegrationId = encodeURIComponent(integrationId)
      const encodedUseCaseId = encodeURIComponent(useCaseId)

      try {
        const response = await c.get<{ history: SecureProxyHistoryRow[] }>(
          `/v2/integrations/${encodedIntegrationId}/use-cases/${encodedUseCaseId}/secure-proxy-whitelist/history`,
          { params: { limit }, ...withOrgHeader(orgId) }
        )

        return response.data.history
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status

          if (status === 404 || status === 501) {
            throw new HistoryEndpointNotAvailableError()
          }
        }

        throw error
      }
    }
  }
}

export const useErpIntegrationClient = () =>
  useMemo(() => getErpIntegrationClient(), [])
